import { Dispatch } from 'redux'
import { setBackPath } from '../there-and-back-again'
import {
  getAuthData,
  getUserSession,
  isSessionValid,
  removeAuthData,
  setAuthData,
} from '../services/session'
import office from '../office/office'
import { messages } from './messages'
import { addBreadcrumb, captureException } from '../sentry-config'
import { resetStore } from '../redux/root'
import { queryClient } from '../queryClient'
import history from '../configureHistory'

export type UserSession = {
  type: string
  auth: {
    isAuthenticated: boolean
    expiresAt: string | boolean
    accessToken: string | boolean
    environment: {
      rudderstack_write_key?: string
      rudderstack_data_plane_url?: string
    }
  }
}

export const setUserSession = (
  expiresAt: string | boolean,
  accessToken: string | boolean,
  environment: {
    rudderstack_write_key?: string
    rudderstack_data_plane_url?: string
  }
): UserSession => ({
  type: 'SET_USER_SESSION',
  auth: {
    isAuthenticated: !!accessToken,
    expiresAt,
    accessToken,
    environment,
  },
})

export const fetchUserSession = () => async (dispatch: Dispatch): Promise<UserSession> => {
  try {
    const authData = await getAuthData()
    const session = await getUserSession(authData)

    if (isSessionValid(session.expiration_date, session.access_token)) {
      setAuthData(session)
      return dispatch(
        setUserSession(session.expiration_date, session.access_token, session.environment)
      )
    }

    removeAuthData()
    addBreadcrumb({
      message: 'userSession: failed to fetch user session',
      level: 'error',
      category: 'request',
    })
  } catch (e) {
    captureException(e)
  }

  return dispatch(setUserSession(false, false, {}))
}

export const deleteUserSession = (): void => {
  removeAuthData()
  queryClient.clear()
  if (office.notifications && office.isCommandContext) {
    try {
      office.notifications.remove('loft-progress')
      office.notifications.addError('sign-in', messages.invalidSession)
      office.terminateApp()
    } catch (e) {
      captureException(new Error('Failed to terminate app safely'), {
        extra: {
          name: 'Sign Out',
          error: e ? JSON.stringify(e) : 'No error details',
        },
      })
    }
  } else {
    setBackPath()
    // Lazy require to avoid a module-load cycle: store -> gateway -> request -> userSession -> store
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const store = require('../store').default
    store.dispatch(resetStore())
    store.dispatch(setUserSession(false, false, {}))
    history.push('/login')
  }
}
